import {useRef} from 'react'
import {getScrollbarSize} from '@/script/scrollbar.mjs'

const isOnScrollbar = (e) => {
    const {width: sw, height: sh} = getScrollbarSize(e.currentTarget)
    const {bottom, right} = e.currentTarget.getBoundingClientRect()

    return (
        right - sw <= e.clientX ||
        bottom - sh <= e.clientY
    )
}

export default (handlers) => {
    const refDown = useRef(null)
    const refMove = useRef(null)
    const refTarget = useRef(null)

    const extendEvent = (e) => {
        const {
            left,
            scrollLeft,
            scrollTop,
            top,
        } = refTarget.current

        const relativeX = e.clientX + scrollLeft - left
        const relativeY = e.clientY + scrollTop - top
        Object.assign(e, {relativeX, relativeY})

        if (refDown.current) {
            const {
                clientX: initialClientX,
                clientY: initialClientY,
                relativeX: initialX,
                relativeY: initialY,
            } = refDown.current

            Object.assign(e, {
                initialClientX,
                initialClientY,
                initialX,
                initialY,
            })
        }
        else {
            Object.assign(e, {
                initialClientX: e.clientX,
                initialClientY: e.clientY,
                initialX: relativeX,
                initialY: relativeY,
            })
        }

        return e
    }

    const onClick = handlers.onClick ?
        e => handlers.onClick(extendEvent(e))
        :
        undefined

    const onClickCapture = handlers.onClickCapture ?
        e => handlers.onClickCapture(extendEvent(e))
        :
        undefined

    const onDoubleClick = handlers.onDoubleClick ?
        e => handlers.onDoubleClick(extendEvent(e))
        :
        void 0

    const onDoubleClickCapture = handlers.onDoubleClickCapture ?
        e => handlers.onDoubleClickCapture(extendEvent(e))
        :
        void 0

    const onPointerDown = (e) => {
        refMove.current = null

        if (isOnScrollbar(e)) {
            return
        }

        const ee = extendEvent(e)
        handlers.onPointerDown?.(ee)
        refDown.current = ee
    }

    const onPointerDownCapture = (e) => {
        const {scrollLeft, scrollTop} = e.currentTarget
        const {left, top} = e.currentTarget.getBoundingClientRect()

        refTarget.current = {
            left,
            scrollLeft,
            scrollTop,
            top,
        }

        handlers.onPointerDownCapture?.(extendEvent(e))
    }

    const onPointerMove = (e) => {
        if (refDown.current) {
            const ee = extendEvent(e)

            if (! refMove.current) {
                e.currentTarget.setPointerCapture(e.pointerId)
                handlers.onSwipeStart?.(ee)
            }

            handlers.onSwipeMove?.(ee)
        }

        refMove.current = e
        handlers.onPointerMove?.(e)
    }

    const onPointerMoveCapture = handlers.onPointerMoveCapture ?
        e => handlers.onPointerMoveCapture(e)
        :
        void 0

    const onPointerUp = (e) => {
        if (! refDown.current) {
            return
        }

        const ee = extendEvent(e)
        ee.buttons = refDown.current.buttons

        if (refMove.current) {
            ee.buttons = refMove.current.buttons
            handlers.onSwipeEnd?.(ee)
            e.currentTarget.releasePointerCapture(e.pointerId)
        }

        handlers.onPointerUp?.(ee)
        refDown.current = null
    }

    const onPointerUpCapture = handlers.onPointerUpCapture ?
        e => handlers.onPointerUpCapture(extendEvent(e))
        :
        void 0

    return {
        onClick,
        onClickCapture,
        onDoubleClick,
        onDoubleClickCapture,
        onPointerDown,
        onPointerDownCapture,
        onPointerMove,
        onPointerMoveCapture,
        onPointerUp,
        onPointerUpCapture,
    }
}
